import { useState, useMemo } from 'react';
import { Search, X } from 'lucide-react';
import { NODE_CATEGORIES } from '../../constants';
import NodeItem from './NodeItem';

export default function NodePanel() {
  const [activeTab, setActiveTab] = useState('all');
  const [search, setSearch] = useState('');

  const filteredCategories = useMemo(() => {
    return NODE_CATEGORIES.map((category) => ({
      ...category,
      nodes: category.nodes.filter((node) =>
        !node.hidden && (search
          ? node.label.toLowerCase().includes(search.toLowerCase()) ||
            node.description?.toLowerCase().includes(search.toLowerCase())
          : true)
      ),
    })).filter((c) => c.nodes.length > 0);
  }, [search]);

  const visibleCategories =
    activeTab === 'all'
      ? filteredCategories
      : filteredCategories.filter((c) => c.key === activeTab);

  return (
    <div className="w-[380px] bg-gray-50 border-r border-gray-200 h-full flex flex-col shrink-0">
      {/* Search */}
      <div className="px-3 pt-3 pb-2">
        <div className="relative">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search nodes..."
            className="w-full pl-8 pr-7 py-2 text-xs border border-gray-200 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-blue-200 focus:border-blue-400"
          />
          {search && (
            <button
              onClick={() => setSearch('')}
              className="absolute right-2 top-1/2 -translate-y-1/2 p-0.5 rounded hover:bg-gray-100"
            >
              <X className="w-3 h-3 text-gray-400" />
            </button>
          )}
        </div>
      </div>

      {/* Category Tabs */}
      <div className="px-3 pb-2 flex gap-1 flex-wrap">
        <button
          onClick={() => setActiveTab('all')}
          className={[
            'px-2.5 py-1 text-[10px] font-semibold rounded-md transition uppercase tracking-wide',
            activeTab === 'all'
              ? 'bg-gray-800 text-white'
              : 'bg-white text-gray-500 border border-gray-200 hover:bg-gray-100',
          ].join(' ')}
        >
          All
        </button>
        {NODE_CATEGORIES.map((category) => (
          <button
            key={category.key}
            onClick={() => setActiveTab(category.key)}
            className={[
              'px-2.5 py-1 text-[10px] font-semibold rounded-md transition uppercase tracking-wide',
              activeTab === category.key
                ? 'text-white'
                : 'bg-white text-gray-500 border border-gray-200 hover:bg-gray-100',
            ].join(' ')}
            style={
              activeTab === category.key
                ? { backgroundColor: category.color }
                : undefined
            }
          >
            {category.label}
          </button>
        ))}
      </div>

      {/* Nodes Grid */}
      <div className="flex-1 overflow-y-auto px-3 pb-3">
        {visibleCategories.length === 0 && (
          <div className="flex flex-col items-center justify-center py-10">
            <p className="text-xs text-gray-400">No nodes found</p>
          </div>
        )}

        {visibleCategories.map((category) => (
          <div key={category.key} className="mb-3">
            {(activeTab === 'all' || search) && (
              <div className="flex items-center gap-2 px-1 py-1.5 mb-1.5">
                <div
                  className="w-1.5 h-1.5 rounded-full shrink-0"
                  style={{ backgroundColor: category.color }}
                />
                <span className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider">
                  {category.label}
                </span>
                <div className="flex-1 h-px bg-gray-200" />
              </div>
            )}
            <div className="grid grid-cols-2 gap-1.5">
              {category.nodes.map((node) => (
                <NodeItem
                  key={node.nodeType}
                  node={node}
                  categoryColor={category.color}
                />
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
